/**
 * The landing page: what is happening right now, and one click into whatever needs looking at.
 *
 * Everything here is a shortcut into a filtered view rather than a dead-end statistic. A tile
 * showing "14 bans today" that cannot be clicked is trivia; one that drops you into the punishment
 * log for today is a starting point. Every number and name on this page navigates.
 *
 * It refreshes on the live stream rather than on a timer — the counters are re-fetched when
 * something actually arrives, throttled so a busy minute does not mean sixty requests.
 */

import { useEffect, useRef } from "react";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { fetchOverview } from "../api";
import { usePlayerCounts } from "./PlayerCounts";
import { formatClock, formatCount, formatRelative, typeColor, typeLabel } from "../format";
import type { Filters } from "../hooks/useUrlFilters";
import type { ConnectionState, Tier } from "../types";

interface Props {
  tier: Tier;
  connection: ConnectionState;
  /** Bumped by App whenever a live event arrives, so the page knows to refresh. */
  liveTick: number;
  go: (next: Partial<Filters>) => void;
  onOpenPlayer: (player: string) => void;
}

/** Shortest gap between live-triggered refreshes. Without it a busy server would refetch constantly. */
const REFRESH_THROTTLE_MS = 15_000;

export function OverviewTab({ tier, connection, liveTick, go, onOpenPlayer }: Props): JSX.Element {
  const queryClient = useQueryClient();
  const lastRefresh = useRef(0);

  const overview = useQuery({
    queryKey: ["overview"],
    queryFn: fetchOverview,
    // A slow fallback for when the live feed is down entirely; the throttled invalidation below is
    // the primary path.
    refetchInterval: 60_000,
  });

  const playerCounts = usePlayerCounts();

  useEffect(() => {
    if (liveTick === 0) return;
    const now = Date.now();
    if (now - lastRefresh.current < REFRESH_THROTTLE_MS) return;
    lastRefresh.current = now;
    void queryClient.invalidateQueries({ queryKey: ["overview"] });
  }, [liveTick, queryClient]);

  const data = overview.data;
  const busiestType = data?.typeBreakdown[0]?.count ?? 1;
  const busiestPlayer = data?.topPlayers[0]?.count ?? 1;

  const today = new Date();
  const todayIso = `${today.getFullYear()}-${String(today.getMonth() + 1).padStart(2, "0")}-${String(today.getDate()).padStart(2, "0")}`;

  return (
    <div className="scroll">
      <div className="pad grid" style={{ gap: 14 }}>
        {/* ---- tiles ------------------------------------------------------------------ */}
        <div className="grid grid-tiles">
          <button
            type="button"
            className="card"
            style={{ textAlign: "left", cursor: "pointer" }}
            onClick={() => go({ tab: "players" })}
          >
            <h2>Players online</h2>
            <div className="tile-n" style={{ color: "var(--ok)", opacity: playerCounts.isError ? 0.6 : 1 }}>
              {playerCounts.data ? playerCounts.data.total : playerCounts.isError ? "—" : "…"}
            </div>
            <div className="tile-l">
              {playerCounts.isError
                ? playerCounts.data
                  ? "Unavailable right now · last known"
                  : "Unavailable right now"
                : playerCounts.data
                  ? `${playerCounts.data.proxies.filter((proxy) => proxy.players > 0).length} proxies with players`
                  : "Loading…"}
            </div>
            <div className="tile-sub">Click for the per-proxy breakdown</div>
          </button>

          <button
            type="button"
            className="card"
            style={{ textAlign: "left", cursor: "pointer" }}
            onClick={() => go({ tab: "logs", types: [], player: "", q: "" })}
          >
            <h2>Events · last hour</h2>
            <div className="tile-n">~{formatCount(data?.activity.lastHour ?? 0)}</div>
            <div className="tile-l">{formatCount(data?.activity.last24h ?? 0)} in 24h</div>
            <div className="tile-sub">{formatCount(data?.activity.totalRows ?? 0)} rows all time</div>
          </button>

          <button
            type="button"
            className="card"
            style={{ textAlign: "left", cursor: "pointer" }}
            onClick={() => go({ tab: "logs", types: ["PUNISHMENT"], from: todayIso, to: "" })}
          >
            <h2>Active bans</h2>
            <div className="tile-n" style={{ color: "var(--danger)" }}>
              {formatCount(data?.punishments.activeBans ?? 0)}
            </div>
            <div className="tile-l">{data?.punishments.bansLast24h ?? 0} issued in 24h</div>
            <div className="tile-sub">Click for today&rsquo;s punishment log</div>
          </button>

          <div className="card">
            <h2>Active mutes</h2>
            <div className="tile-n" style={{ color: "var(--warn)" }}>
              {formatCount(data?.punishments.activeMutes ?? 0)}
            </div>
            <div className="tile-l">{data?.punishments.mutesLast24h ?? 0} issued in 24h</div>
            <div className="tile-sub">Mute history has no unmute record</div>
          </div>

          {tier !== "viewer" && (
            <button
              type="button"
              className="card"
              style={{ textAlign: "left", cursor: "pointer" }}
              onClick={() => go({ tab: "voice" })}
            >
              <h2>Voice flags · 24h</h2>
              <div className="tile-n">{formatCount(data?.voice?.last24h ?? 0)}</div>
              <div className="tile-l">{data?.voice?.punishedLast24h ?? 0} led to a punishment</div>
              <div className="tile-sub">Click to open the voice log</div>
            </button>
          )}
        </div>

        {/* ---- quick actions ---------------------------------------------------------- */}
        <div className="card">
          <h2>Jump to</h2>
          <div className="quick">
            <button type="button" className="btn" onClick={() => go({ tab: "logs", types: ["PUNISHMENT"] })}>
              Punishments
            </button>
            <button type="button" className="btn" onClick={() => go({ tab: "logs", types: ["CHAT", "CHAT_FILTER"] })}>
              Chat &amp; filter
            </button>
            <button type="button" className="btn" onClick={() => go({ tab: "logs", types: ["PAYMENT", "ORDER", "AUCTION", "WAGERS"] })}>
              Economy
            </button>
            <button type="button" className="btn" onClick={() => go({ tab: "logs", types: ["DEATH", "KILL"] })}>
              Combat
            </button>
            <button type="button" className="btn" onClick={() => go({ tab: "logs", types: ["ADMIN"] })}>
              Admin actions
            </button>
            <button type="button" className="btn" onClick={() => go({ tab: "logs", types: [], from: todayIso })}>
              Today only
            </button>
          </div>
        </div>

        <div className="grid grid-2">
          {/* ---- type breakdown ------------------------------------------------------- */}
          <div className="card">
            <h2>
              By type · last hour <span className="grow" />
            </h2>
            {(data?.typeBreakdown.length ?? 0) === 0 ? (
              <div className="tile-sub">Nothing logged in the last hour.</div>
            ) : (
              <div className="bars">
                {data?.typeBreakdown.slice(0, 9).map((entry) => (
                  <button
                    key={entry.type}
                    type="button"
                    className="bar"
                    style={{
                      ["--chip" as string]: typeColor(entry.type),
                      background: "none",
                      border: "none",
                      padding: 0,
                      cursor: "pointer",
                      textAlign: "left",
                    }}
                    onClick={() => go({ tab: "logs", types: [entry.type as never] })}
                  >
                    <span className="bar-name">{typeLabel(entry.type)}</span>
                    <span className="bar-track">
                      <span
                        className="bar-fill"
                        style={{ width: `${Math.max(2, (entry.count / busiestType) * 100)}%` }}
                      />
                    </span>
                    <span className="bar-n">{formatCount(entry.count)}</span>
                  </button>
                ))}
              </div>
            )}
          </div>

          {/* ---- busiest players ------------------------------------------------------ */}
          <div className="card">
            <h2>Busiest players · last hour</h2>
            {(data?.topPlayers.length ?? 0) === 0 ? (
              <div className="tile-sub">Nobody has done anything in the last hour.</div>
            ) : (
              <div className="bars">
                {data?.topPlayers.map((entry) => (
                  <button
                    key={entry.player}
                    type="button"
                    className="bar"
                    style={{
                      background: "none",
                      border: "none",
                      padding: 0,
                      cursor: "pointer",
                      textAlign: "left",
                    }}
                    onClick={() => onOpenPlayer(entry.player)}
                  >
                    <span className="bar-name" style={{ color: "var(--accent)" }}>
                      {entry.player}
                    </span>
                    <span className="bar-track">
                      <span
                        className="bar-fill"
                        style={{ width: `${Math.max(2, (entry.count / busiestPlayer) * 100)}%` }}
                      />
                    </span>
                    <span className="bar-n">{formatCount(entry.count)}</span>
                  </button>
                ))}
              </div>
            )}
          </div>

          {/* ---- recent punishments --------------------------------------------------- */}
          <div className="card">
            <h2>Recent punishments</h2>
            {(data?.recentPunishments.length ?? 0) === 0 ? (
              <div className="tile-sub">Nothing on record.</div>
            ) : (
              <div className="feed">
                {data?.recentPunishments.map((entry, index) => (
                  <div className="feed-item" key={`${entry.kind}-${entry.player}-${entry.issued_at}-${index}`}>
                    <span className="tag" data-k={entry.kind}>
                      {entry.kind}
                    </span>
                    <div className="feed-body">
                      <button type="button" className="who" onClick={() => onOpenPlayer(entry.player)}>
                        {entry.player}
                      </button>
                      <div className="r">
                        {entry.reason} &middot; by {entry.issuer}
                      </div>
                    </div>
                    {entry.active && (
                      <span className="tag" data-k="active">
                        active
                      </span>
                    )}
                    <span className="feed-when">{formatRelative(entry.issued_at)}</span>
                  </div>
                ))}
              </div>
            )}
          </div>

          {/* ---- voice, staff and above ------------------------------------------------ */}
          {tier !== "viewer" && (
            <div className="card">
              <h2>Recent voice flags</h2>
              {(data?.voice?.recent.length ?? 0) === 0 ? (
                <div className="tile-sub">
                  Nothing flagged yet. Voice events only appear once the updated plugin is running.
                </div>
              ) : (
                <div className="feed">
                  {data?.voice?.recent.map((event) => (
                    <div className="feed-item" key={event.id}>
                      <span className="tag" data-k={event.punished ? "ban" : "kick"}>
                        {event.punished ? "punished" : "logged"}
                      </span>
                      <div className="feed-body">
                        <button type="button" className="who" onClick={() => onOpenPlayer(event.player)}>
                          {event.player}
                        </button>
                        <div className="r">
                          {event.matched_rule ?? "no rule"} {event.has_clip ? "· clip available" : "· no clip"}
                        </div>
                      </div>
                      <span className="feed-when">{formatClock(event.occurred_at)}</span>
                    </div>
                  ))}
                </div>
              )}
            </div>
          )}
        </div>

        {/* ---- system ----------------------------------------------------------------- */}
        <div className="card">
          <h2>System</h2>
          <div className="grid grid-tiles" style={{ gap: 18 }}>
            <div>
              <div className="conn" data-state={connection}>
                <span className="dot" />
                {connection === "live"
                  ? "Live feed healthy"
                  : connection === "degraded-polling"
                    ? "Degraded — polling MySQL"
                    : connection === "reconnecting"
                      ? "Reconnecting to Redis"
                      : "Live tail off"}
              </div>
              <div className="tile-sub">
                {connection === "degraded-polling"
                  ? "Redis is unreachable. Events still arrive, roughly every 3 seconds."
                  : "Events arrive within about a second of happening in game."}
              </div>
            </div>
            <div>
              <div className="tile-n" style={{ fontSize: 19 }}>
                {data?.system.streams.total ?? 0}
              </div>
              <div className="tile-l">open streams</div>
            </div>
            <div>
              <div className="tile-n" style={{ fontSize: 19 }}>
                {Math.floor((data?.system.uptimeSeconds ?? 0) / 3600)}h
              </div>
              <div className="tile-l">dashboard uptime</div>
            </div>
          </div>
        </div>

        {overview.isError && (
          <div className="note" data-k="danger">
            Could not load the overview. The log list may still work — check the dashboard&rsquo;s
            console output.
          </div>
        )}
      </div>
    </div>
  );
}
